import React, { useState } from 'react';
import { API_BASE } from '../../api';

function ClientFeedbackModal({ show, onClose, onSubmit, task, primaryColor, currentClientId }) {
    const [provedStatus, setProvedStatus] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Reset form when modal opens
    React.useEffect(() => {
        if (show) {
            setProvedStatus('');
            setDescription('');
            setError('');
        }
    }, [show]);

    if (!show) {
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!provedStatus) {
            setError('Please select an approval status');
            return;
        }

        if (provedStatus === 'not proved' && !description.trim()) {
            setError('Please provide feedback for revision requests');
            return;
        }

        setIsSubmitting(true);
        setError('');
        
        try {
            // Debug log
            console.log('Submitting feedback:', {
                task_id: task.id,
                client_id: currentClientId,
                proved_status: provedStatus,
                description: description.trim(),
                video_name: task.video_name
            });
            
            // Call the API to submit feedback
            const response = await fetch(`${API_BASE}/submit_client_feedback.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    task_id: task.id,
                    client_id: parseInt(currentClientId),
                    proved_status: provedStatus,
                    description: description.trim(),
                    video_name: task.video_name || 'Untitled Task'
                })
            });

            const result = await response.json();
            
            console.log('API Response:', result); // Debug log

            if (!result.success) {
                throw new Error(result.error || 'Failed to submit feedback');
            }

            // Call parent onSubmit with success
            await onSubmit(task.id, provedStatus, description.trim(), result);
            
            // Show success message
            alert('✅ Feedback submitted successfully!');
            
            // Close modal after successful submission
            handleClose();
            
        } catch (error) {
            console.error('Error details:', error);
            setError(error.message || 'An error occurred while submitting feedback');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setProvedStatus('');
            setDescription('');
            setError('');
            onClose();
        }
    };

    return (
        <div 
            className="modal fade show" 
            style={{ 
                display: 'block', 
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1050
            }} 
            tabIndex="-1"
            role="dialog"
        >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px' }}>
                <div className="modal-content" style={{
                    background: '#FFFFFF',
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
                }}>
                    <div className="modal-header" style={{ 
                        background: primaryColor, 
                        color: 'white',
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        padding: '15px 20px'
                    }}>
                        <h5 className="modal-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                            <i className="fas fa-edit me-2"></i>
                            Review Video: {task?.video_name || 'Untitled Task'}
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close btn-close-white" 
                            onClick={handleClose} 
                            disabled={isSubmitting}
                            aria-label="Close"
                            style={{ 
                                margin: 0,
                                backgroundSize: '1rem',
                                opacity: 1
                            }}
                        ></button>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body" style={{ 
                            background: '#FFFFFF',
                            padding: '20px'
                        }}>
                            {error && (
                                <div className="alert alert-danger" role="alert" style={{
                                    background: '#F8D7DA',
                                    color: '#721C24',
                                    border: '1px solid #F5C6CB',
                                    borderRadius: '6px',
                                    padding: '10px 15px',
                                    marginBottom: '15px',
                                    fontSize: '0.9rem'
                                }}>
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    {error}
                                </div>
                            )}
                            
                            {/* Video Preview Link */}
                            {(task?.edited_video_link || task?.edited_link) && (
                                <div className="mb-4" style={{ marginBottom: '20px' }}>
                                    <label className="form-label fw-bold" style={{ 
                                        color: '#333',
                                        fontSize: '0.9rem',
                                        marginBottom: '8px',
                                        display: 'block'
                                    }}>Video Preview</label>
                                    <div style={{ 
                                        border: '1px solid #E0E0E0',
                                        borderRadius: '6px',
                                        padding: '15px',
                                        background: '#F8F9FA'
                                    }}>
                                        <a 
                                            href={task.edited_video_link || task.edited_link} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="btn btn-primary btn-sm"
                                            style={{
                                                background: primaryColor,
                                                border: 'none',
                                                padding: '8px 16px',
                                                borderRadius: '4px',
                                                fontSize: '0.8rem',
                                                fontWeight: '600',
                                                textDecoration: 'none',
                                                display: 'inline-flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <i className="fas fa-video me-2"></i>
                                            Watch Final Video
                                        </a>
                                        <small className="text-muted d-block mt-2" style={{
                                            color: '#6C757D',
                                            fontSize: '0.75rem',
                                            marginTop: '8px',
                                            display: 'block'
                                        }}>
                                            Review the video before submitting feedback
                                        </small>
                                    </div>
                                </div>
                            )}
                            
                            <div className="mb-4" style={{ marginBottom: '20px' }}>
                                <label className="form-label fw-bold" style={{ 
                                    color: '#333',
                                    fontSize: '0.9rem',
                                    marginBottom: '8px',
                                    display: 'block'
                                }}>Approval Status *</label>
                                <div className="row g-2" style={{ marginLeft: '-4px', marginRight: '-4px' }}>
                                    <div className="col" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                                        <button
                                            type="button"
                                            className={`btn w-100 ${provedStatus === 'proved' ? 'btn-success' : 'btn-outline-success'}`}
                                            onClick={() => setProvedStatus('proved')}
                                            disabled={isSubmitting}
                                            style={{
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: provedStatus === 'proved' ? 'none' : '1px solid #28A745',
                                                background: provedStatus === 'proved' ? '#28A745' : 'transparent',
                                                color: provedStatus === 'proved' ? 'white' : '#28A745',
                                                fontWeight: '600',
                                                fontSize: '0.85rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <i className="fas fa-thumbs-up me-2"></i>
                                            Approve (Proved)
                                        </button>
                                    </div>
                                    <div className="col" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
                                        <button
                                            type="button"
                                            className={`btn w-100 ${provedStatus === 'not proved' ? 'btn-danger' : 'btn-outline-danger'}`}
                                            onClick={() => setProvedStatus('not proved')}
                                            disabled={isSubmitting}
                                            style={{
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: provedStatus === 'not proved' ? 'none' : '1px solid #DC3545',
                                                background: provedStatus === 'not proved' ? '#DC3545' : 'transparent',
                                                color: provedStatus === 'not proved' ? 'white' : '#DC3545',
                                                fontWeight: '600',
                                                fontSize: '0.85rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <i className="fas fa-thumbs-down me-2"></i>
                                            Needs Revision
                                        </button>
                                    </div>
                                </div>
                                <div className="form-text mt-2" style={{
                                    color: '#6C757D',
                                    fontSize: '0.75rem',
                                    marginTop: '8px'
                                }}>
                                    Select "Approve" if the video is ready for delivery, or "Needs Revision" if changes are required.
                                </div>
                            </div>
                            
                            <div className="mb-4" style={{ marginBottom: '20px' }}>
                                <label htmlFor="description" className="form-label fw-bold" style={{ 
                                    color: '#000000', // Changed to black for better visibility
                                    fontSize: '0.9rem',
                                    marginBottom: '8px',
                                    display: 'block'
                                }}>
                                    Feedback & Comments {provedStatus === 'not proved' && <span className="text-danger" style={{ color: '#DC3545' }}>*</span>}
                                </label>
                                <textarea 
                                    className="form-control" 
                                    id="description" 
                                    rows="4" 
                                    placeholder={
                                        provedStatus === 'proved' 
                                            ? "Optional: Add any comments or feedback..." 
                                            : "Required: Please specify what needs to be revised..."
                                    }
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={isSubmitting}
                                    required={provedStatus === 'not proved'}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #E0E0E0',
                                        background: '#FFFFFF', // White background
                                        color: '#000000', // Black text for good contrast
                                        fontSize: '0.85rem',
                                        resize: 'vertical',
                                        minHeight: '100px'
                                    }}
                                ></textarea>
                                <div className="form-text" style={{
                                    color: '#000000', // Changed to black for better visibility
                                    fontSize: '0.75rem',
                                    marginTop: '6px',
                                    fontWeight: '500'
                                }}>
                                    {provedStatus === 'not proved' 
                                        ? "Detailed feedback helps the editor understand what needs to be changed."
                                        : "Your feedback helps us improve our service."
                                    }
                                </div>
                            </div>
                        </div>
                        
                        <div className="modal-footer" style={{ 
                            background: '#F8F9FA',
                            borderTop: '1px solid #E0E0E0',
                            padding: '15px 20px',
                            borderBottomLeftRadius: '8px',
                            borderBottomRightRadius: '8px'
                        }}>
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={handleClose}
                                disabled={isSubmitting}
                                style={{
                                    background: '#6C757D',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    opacity: isSubmitting ? 0.6 : 1
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={isSubmitting || !provedStatus}
                                style={{ 
                                    background: primaryColor, 
                                    borderColor: primaryColor,
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    cursor: isSubmitting || !provedStatus ? 'not-allowed' : 'pointer',
                                    opacity: isSubmitting || !provedStatus ? 0.6 : 1
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" style={{
                                            width: '0.9rem',
                                            height: '0.9rem',
                                            borderWidth: '2px'
                                        }}></span>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane me-2"></i>
                                        Submit Feedback
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ClientFeedbackModal;